import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  Box,
  ButtonIcon,
  Icon,
  InlineMessage,
  MobileDrawer,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { setConsultActionAssigneeLoading } from '../../../redux/certdetails/action';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
} from '../../../utils/constant';
import { useInvertedLocalize } from '../../../utils/localization';
import { getMessageKey } from '../../../utils/utils';
import { invertedMessages } from '../messages';
import { BULK_ACTION_CONSULT } from '../mockData';

import BulkActionConsultBody from './BulkActionConsultBody';
import BulkActionConsultFooter from './BulkActionConsultFooter';
import { TRANSITION_TIMEOUT } from './constant';

import './BulkActionConsult.css';

function BulkActionConsult({
  bulkFooterStats,
  bulkActionConsult,
  setBulkActionConsult,
  handleConsult,
  bulkPredefinedComments,
  isMobileXlargeWidth,
}) {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { messages } = useSelector((state) => state?.user);
  const l = useInvertedLocalize(invertedMessages);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  const {
    core: { campaignType },
    lineActions: { reassignConfigMap },
  } = useSelector((state) => state?.certifications?.details);
  const isEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;
  const { showConsultForOwnAccessNote } = reassignConfigMap;

  const renderConsultNote = () => {
    if (isEOCampaign && Boolean(showConsultForOwnAccessNote)) {
      return (
        <InlineMessage
          className='BulkActionConsult-inlineMessage'
          text={l(
            'Action will not be applied for access items that belong to the certifier.'
          )}
          colorTheme='info'
          size='medium'
          leftIcon={<Icon kind='AlertInfoFilled' />}
        />
      );
    }

    return null;
  };

  const handleCloseBulkActionConsult = () => {
    setTimeout(() => {
      dispatch(setConsultActionAssigneeLoading(true));
    }, TRANSITION_TIMEOUT);

    setBulkActionConsult((prev) => ({
      ...prev,
      isOpen: false,
      hasAgreed: false,
      reassignAssociated: false,
      consultWith: '',
      comment: '',
    }));
  };

  const handleCloseBukActionConsultAlert = () => {
    setBulkActionConsult((prev) => ({
      ...prev,
      showAlert: false,
    }));
  };

  const bulkActionConsultModal = classnames('BulkActionConsultModal');

  useEffect(() => {
    if (!isMobileDrawerOpen && isMobileXlargeWidth)
      handleCloseBulkActionConsult();
  }, [isMobileDrawerOpen]);

  useEffect(() => {
    setIsMobileDrawerOpen(bulkActionConsult?.isOpen);
  }, [bulkActionConsult?.isOpen]);

  // TODO: @Sagar, These classNames are very important to update!!
  if (isMobileXlargeWidth) {
    return (
      <MobileDrawer
        className='BulkActionConsult-mobileDrawer'
        isOpen={isMobileDrawerOpen}
        setIsOpen={setIsMobileDrawerOpen}
        headerContent={
          <div className='ModalIsland-headerWrapper'>
            <div className='ModalIsland-header ModalIsland-header--hasSubtitle ModalIsland-header--hasIcon'>
              <Icon kind='Consult' size='large' />
              <div className='ModalIsland-titleContainer'>
                <Typography
                  className='ModalIsland-title'
                  kind='h2'
                  desktop='h2-d'
                  mobile='h2-d'>
                  {messages['BULKACTION.consult.title.ATTESTATIONS'] ||
                    localize(intl, getMessageKey('BULKACTION.consult.title'))}
                </Typography>
                <Typography
                  className='ModalIsland-subtitle'
                  kind='body2'
                  desktop='body2-d'
                  mobile='body2-d'>
                  {messages['BULKACTION.consult.subTitle.ATTESTATIONS'] ||
                    localize(
                      intl,
                      getMessageKey('BULKACTION.consult.subTitle')
                    )}
                </Typography>
                {renderConsultNote()}
              </div>
            </div>
            <Box tag='div' className='ModalIsland-closeBtn'>
              <ButtonIcon
                size='medium'
                kind='ghost'
                ariaLabel={l('Close')}
                icon={
                  <Icon kind='close' color='neutral-100' size='smallMedium' />
                }
                onClick={() => setIsMobileDrawerOpen(false)}
              />
            </Box>
          </div>
        }
        content={
          <BulkActionConsultBody
            bulkFooterStats={bulkFooterStats}
            bulkActionConsult={bulkActionConsult}
            setBulkActionConsult={setBulkActionConsult}
            handleCloseBulkActionConsult={handleCloseBulkActionConsult}
            handleCloseBukActionConsultAlert={handleCloseBukActionConsultAlert}
            bulkPredefinedComments={bulkPredefinedComments}
          />
        }
        footerActionBar={
          <BulkActionConsultFooter
            handleConsult={handleConsult}
            bulkActionConsult={bulkActionConsult}
            handleCloseBulkActionConsult={handleCloseBulkActionConsult}
          />
        }
      />
    );
  }

  return (
    <ModalIsland
      className={bulkActionConsultModal}
      onClose={
        bulkActionConsult?.isSubmitting
          ? () => {}
          : handleCloseBulkActionConsult
      }
      isOpen={bulkActionConsult?.isOpen}
      headerIcon={<Icon kind='Consult' size='large' />}
      title={
        messages['BULKACTION.consult.title.ATTESTATIONS'] ||
        localize(intl, getMessageKey('BULKACTION.consult.title'))
      }
      subtitle={
        <Box>
          {messages['BULKACTION.consult.subTitle.ATTESTATIONS'] ||
            localize(intl, getMessageKey('BULKACTION.consult.subTitle'))}
          {renderConsultNote()}
        </Box>
      }
      contentBody={
        <BulkActionConsultBody
          bulkFooterStats={bulkFooterStats}
          bulkActionConsult={bulkActionConsult}
          setBulkActionConsult={setBulkActionConsult}
          handleCloseBulkActionConsult={handleCloseBulkActionConsult}
          handleCloseBukActionConsultAlert={handleCloseBukActionConsultAlert}
          bulkPredefinedComments={bulkPredefinedComments}
        />
      }
      FooterActionBarComp={
        <BulkActionConsultFooter
          handleConsult={handleConsult}
          bulkActionConsult={bulkActionConsult}
          handleCloseBulkActionConsult={handleCloseBulkActionConsult}
        />
      }
    />
  );
}

BulkActionConsult.propTypes = {
  bulkFooterStats: PropTypes.shape({}),
  bulkActionConsult: PropTypes.shape(BULK_ACTION_CONSULT).isRequired,
  setBulkActionConsult: PropTypes.func.isRequired,
  handleConsult: PropTypes.func.isRequired,
  bulkPredefinedComments: PropTypes.arrayOf(PropTypes.string),
  isMobileXlargeWidth: PropTypes.bool,
};

BulkActionConsult.defaultProps = {
  bulkFooterStats: {},
  bulkPredefinedComments: [],
  isMobileXlargeWidth: false,
};

export default BulkActionConsult;
